import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Check, ShoppingCart, Link2 } from 'lucide-react';
import LinkOrderTargetModal, { OrderLinkSelection } from './LinkOrderTargetModal';
import { StockItem } from '../lib/stockItems';
import { getSuppliers, Customer } from '../lib/customers';
import { useBusiness } from '../contexts/BusinessContext';
import OrderSummaryPDFModal from './OrderSummaryPDFModal';
import { createOrder, Order, OrderItem, getOnOrderQuantities, updateOrderEmailInfo, linkEmailToOrder } from '../lib/purchaseOrders';
import { toast } from '../components/toasts/SimpleToast';
import OneTimeItemOrderModal, { CustomOrderItemResult } from './OneTimeItemOrderModal';

interface CreateOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  items: StockItem[];
  preselectedSupplierId?: string | null;
  onItemsRefresh?: () => void;
  onOrderQuantities?: Record<string, number>;
  projectId?: string | null; // Link order to a project
  onOrderCreated?: (orderId: string) => void | Promise<void>;
}

interface OrderItem {
  item: StockItem;
  quantityToOrder: number;
  totalCost: number;
  comment?: string | null;
  images?: string[] | null;
}

export default function CreateOrderModal({
  isOpen,
  onClose,
  items,
  preselectedSupplierId,
  onItemsRefresh,
  onOrderQuantities = {},
  projectId = null,
  onOrderCreated
}: CreateOrderModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState<Customer[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<Customer | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState<Map<string, number>>(new Map()); // itemId -> quantity
  const [itemComments, setItemComments] = useState<Map<string, string>>(new Map()); // itemId -> supplier-facing comment
  const [itemImages, setItemImages] = useState<Map<string, string[]>>(new Map()); // itemId -> reference image URLs
  const [orderSummary, setOrderSummary] = useState<{
    supplier: Customer;
    items: OrderItem[];
    totalAmount: number;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOrderSummaryModal, setShowOrderSummaryModal] = useState(false);
  const [isNewStockItemModalOpen, setIsNewStockItemModalOpen] = useState(false);
  const [pendingItemId, setPendingItemId] = useState<string | null>(null);
  const [pendingItemQuantity, setPendingItemQuantity] = useState<number>(1);
  const [shouldAutoOpenSummary, setShouldAutoOpenSummary] = useState(false);
  const [temporaryItems, setTemporaryItems] = useState<StockItem[]>([]); // For items not yet in items prop
  // Custom-order flow: which items came from the Create Custom Order screen,
  // whether the summary was opened from that flow (so Back returns there), and
  // the pre-filled items when reopening the custom screen in edit mode.
  const [customOrderItemIds, setCustomOrderItemIds] = useState<string[]>([]);
  const [customOrderEditItems, setCustomOrderEditItems] = useState<CustomOrderItemResult[] | null>(null);
  const [summaryFromCustomOrder, setSummaryFromCustomOrder] = useState(false);
  const [localOnOrderQuantities, setLocalOnOrderQuantities] = useState<Record<string, number>>(onOrderQuantities);
  const hasCalledOnItemsRefresh = useRef(false);

  const isMobile = window.innerWidth < 768;
  
  // Use local state if prop is empty, otherwise use prop
  const effectiveOnOrderQuantities = useMemo(() => {
    if (Object.keys(onOrderQuantities).length > 0) {
      return onOrderQuantities;
    }
    return localOnOrderQuantities;
  }, [onOrderQuantities, localOnOrderQuantities]);
  
  // Load on-order quantities if prop is empty
  useEffect(() => {
    if (isOpen && Object.keys(onOrderQuantities).length === 0 && selectedBusiness?.id) {
      console.log('📦 CreateOrderModal: onOrderQuantities prop is empty, fetching locally...');
      getOnOrderQuantities(selectedBusiness.id).then(quantities => {
        console.log('📦 CreateOrderModal: Loaded on-order quantities locally:', quantities);
        setLocalOnOrderQuantities(quantities);
      }).catch(err => {
        console.error('Error loading on-order quantities in modal:', err);
      });
    } else if (isOpen && Object.keys(onOrderQuantities).length > 0) {
      // Update local state when prop updates
      setLocalOnOrderQuantities(onOrderQuantities);
    }
  }, [isOpen, onOrderQuantities, selectedBusiness?.id]);

  // Load suppliers
  const loadSuppliers = useCallback(async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      const data = await getSuppliers(selectedBusiness.id);
      setSuppliers(data);
    } catch (err) {
      console.error('Error loading suppliers:', err);
      toast.error({
        title: 'Error',
        description: 'Failed to load suppliers',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness?.id]);

  // Initialize when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadSuppliers();
      // Reset state
      setSearchQuery('');
      setSelectedItems(new Map());
      setItemComments(new Map());
      setItemImages(new Map());
      setOrderSummary(null);
      setPendingItemId(null);
      setPendingItemQuantity(1);
      setShouldAutoOpenSummary(false);
      setTemporaryItems([]);
      setCustomOrderItemIds([]);
      setCustomOrderEditItems(null);
      setSummaryFromCustomOrder(false);
      // Reset the ref when modal opens
      hasCalledOnItemsRefresh.current = false;
    } else {
      // Reset ref when modal closes
      hasCalledOnItemsRefresh.current = false;
    }
  }, [isOpen, loadSuppliers]);
  
  // Call onItemsRefresh once when modal opens (separate effect to avoid infinite loop)
  useEffect(() => {
    if (isOpen && onItemsRefresh && !hasCalledOnItemsRefresh.current) {
      console.log('🔄 CreateOrderModal: Calling onItemsRefresh to reload items and onOrderQuantities');
      hasCalledOnItemsRefresh.current = true;
      onItemsRefresh();
    }
  }, [isOpen, onItemsRefresh]);
  
  // Log when onOrderQuantities prop changes and force re-filter
  useEffect(() => {
    if (isOpen) {
      console.log('📊 CreateOrderModal: onOrderQuantities prop updated:', onOrderQuantities);
      console.log('📊 CreateOrderModal: onOrderQuantities keys:', Object.keys(onOrderQuantities));
      // Force a re-render by updating a dummy state if needed
      // The useMemo for supplierItems should automatically recalculate when onOrderQuantities changes
    }
  }, [isOpen, onOrderQuantities]);

  // Watch for new items being added (for 1-time items)
  useEffect(() => {
    if (pendingItemId && selectedSupplier) {
      const newItem = items.find(i => i.id === pendingItemId);
      console.log('🔍 Checking for pending item:', { 
        pendingItemId, 
        newItem: newItem ? { id: newItem.id, name: newItem.name, supplier_id: newItem.supplier_id } : null, 
        selectedSupplierId: selectedSupplier.id,
        itemsCount: items.length,
        pendingItemQuantity
      });
      
      if (newItem && newItem.supplier_id === selectedSupplier.id) {
        console.log('✅ Adding newly created one-time item to order:', newItem.name, 'with quantity:', pendingItemQuantity);
        setSelectedItems(prev => {
          const updated = new Map(prev);
          updated.set(pendingItemId, pendingItemQuantity); // Use the quantity from the modal
          console.log('📦 Updated selectedItems:', Array.from(updated.entries()));
          return updated;
        });
        setPendingItemId(null); // Clear pending item
        setPendingItemQuantity(1); // Reset quantity
        setShouldAutoOpenSummary(true); // Flag to auto-open summary
        console.log('🚀 Set shouldAutoOpenSummary to true');
        
        toast.success({
          title: 'Item Added',
          description: `${newItem.name} has been added to your order`,
          duration: 3000,
        });
      } else if (newItem && newItem.supplier_id !== selectedSupplier.id) {
        console.warn('⚠️ New item supplier does not match selected supplier', { 
          itemSupplierId: newItem.supplier_id, 
          selectedSupplierId: selectedSupplier.id 
        });
        setPendingItemId(null);
        setPendingItemQuantity(1);
      } else if (!newItem) {
        console.log('⏳ Waiting for item to appear in items list...', { 
          itemsCount: items.length,
          pendingItemId,
          itemIds: items.map(i => i.id)
        });
      }
    }
  }, [items, pendingItemId, pendingItemQuantity, selectedSupplier]);

  // Automatically open order summary when orderSummary is ready and we just added a one-time item
  useEffect(() => {
    if (shouldAutoOpenSummary && orderSummary && orderSummary.items.length > 0 && !showOrderSummaryModal) {
      // Short delay so the summary is fully calculated — kept minimal so the
      // Create New Order screen doesn't visibly flash between steps.
      const timer = setTimeout(() => {
        console.log('📋 Opening order summary automatically for one-time item', { 
          orderSummary, 
          itemsCount: orderSummary.items.length 
        });
        setShowOrderSummaryModal(true);
        setShouldAutoOpenSummary(false); // Reset flag
      }, 150);
      
      return () => clearTimeout(timer);
    } else if (shouldAutoOpenSummary && !orderSummary) {
      console.log('⏳ Waiting for order summary to be calculated...', { 
        selectedItemsSize: selectedItems.size,
        selectedSupplier: selectedSupplier?.id 
      });
    }
  }, [shouldAutoOpenSummary, orderSummary, showOrderSummaryModal, selectedItems.size, selectedSupplier]);

  // Pre-select supplier - runs when modal opens or suppliers/preselectedSupplierId changes
  useEffect(() => {
    if (!isOpen) {
      // Reset when modal closes
      setSelectedSupplier(null);
      return;
    }
    
    // When modal opens, set supplier if preselectedSupplierId is provided
    if (suppliers.length > 0) {
      if (preselectedSupplierId) {
        const supplier = suppliers.find(s => s.id === preselectedSupplierId);
        setSelectedSupplier(supplier || null);
      } else {
        setSelectedSupplier(null);
      }
    }
  }, [isOpen, preselectedSupplierId, suppliers]);

  // Calculate order summary
  // Merge items prop with temporaryItems to include newly created items
  const allItems = useMemo(() => {
    const merged = [...items];
    temporaryItems.forEach(tempItem => {
      if (!merged.find(i => i.id === tempItem.id)) {
        merged.push(tempItem);
      }
    });
    return merged;
  }, [items, temporaryItems]);

  // Filter items by selected supplier
  // Include one-time items if they're already selected in the order OR if they're pending (just created)
  // OR if they have items on order
  // Use useMemo to recalculate when onOrderQuantities changes
  const supplierItems = useMemo(() => {
    if (!selectedSupplier) return [];
    
    console.log('🔍 Filtering supplierItems:', {
      selectedSupplierId: selectedSupplier.id,
      allItemsCount: allItems.length,
      onOrderQuantities: onOrderQuantities,
      onOrderQuantitiesKeys: Object.keys(onOrderQuantities),
      allOneTimeItems: allItems.filter(i => i.is_one_time).map(i => ({
        id: i.id,
        name: i.name,
        supplier_id: i.supplier_id,
        quantity: i.quantity,
        onOrder: onOrderQuantities[i.id] || 0,
        matchesSupplier: i.supplier_id === selectedSupplier.id
      }))
    });
    
    return allItems.filter(item => {
      if (item.is_deleted) {
        console.log(`❌ Excluding deleted item: ${item.name}`);
        return false;
      }
      
      // Check supplier match first
      if (item.supplier_id !== selectedSupplier.id) {
        // For one-time items with orders, we still want to show them even if supplier doesn't match initially
        // But actually, the supplier_id should match since it was set when the item was created
        if (item.is_one_time) {
          const onOrderQty = onOrderQuantities[item.id] || 0;
          if (onOrderQty > 0) {
            console.log(`⚠️ One-time item "${item.name}" (id: ${item.id}) has ${onOrderQty} on order but supplier_id (${item.supplier_id}) doesn't match selected (${selectedSupplier.id})`);
          }
        }
        return false;
      }
      
      // Include one-time items if they're already in selectedItems OR if they're the pending item
      // OR if they have items on order (even with 0 quantity)
      if (item.is_one_time) {
        const onOrderQty = effectiveOnOrderQuantities[item.id] || 0;
        const shouldInclude = selectedItems.has(item.id) || item.id === pendingItemId || onOrderQty > 0;
        console.log(`🔍 One-time item "${item.name}" (id: ${item.id}): quantity=${item.quantity}, onOrder=${onOrderQty}, shouldInclude=${shouldInclude}`);
        if (item.quantity === 0 && onOrderQty > 0) {
          console.log(`✅ Including one-time item "${item.name}" (id: ${item.id}) with ${onOrderQty} units on order for supplier ${selectedSupplier.id}`);
        }
        return shouldInclude;
      }
      // Regular items are always shown
      return true;
    });
  }, [selectedSupplier, allItems, effectiveOnOrderQuantities, selectedItems, pendingItemId]);
  
  // Debug logging
  useEffect(() => {
    if (isOpen && selectedSupplier) {
      console.log('🔍 CreateOrderModal filter debug:', {
        selectedSupplierId: selectedSupplier.id,
        allItemsCount: allItems.length,
        supplierItemsCount: supplierItems.length,
        onOrderQuantities: effectiveOnOrderQuantities,
        onOrderQuantitiesKeys: Object.keys(effectiveOnOrderQuantities),
        oneTimeItemsInAllItems: allItems.filter(i => i.is_one_time).map(i => ({ 
          id: i.id, 
          name: i.name, 
          quantity: i.quantity, 
          supplier_id: i.supplier_id,
          onOrder: effectiveOnOrderQuantities[i.id] || 0 
        })),
        supplierItemsOneTime: supplierItems.filter(i => i.is_one_time).map(i => ({ 
          id: i.id, 
          name: i.name, 
          quantity: i.quantity,
          onOrder: effectiveOnOrderQuantities[i.id] || 0 
        }))
      });
    }
  }, [isOpen, selectedSupplier, allItems, supplierItems, onOrderQuantities]);

  // Filter items by search query
  const filteredItems = searchQuery
    ? supplierItems.filter(item =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.sku?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : supplierItems;

  useEffect(() => {
    if (selectedSupplier && selectedItems.size > 0) {
      const orderItems: OrderItem[] = [];
      let totalAmount = 0;

      selectedItems.forEach((quantity, itemId) => {
        const item = allItems.find(i => i.id === itemId);
        if (item && quantity > 0) {
          const totalCost = quantity * item.acquisition_cost;
          orderItems.push({
            item,
            quantityToOrder: quantity,
            totalCost,
            comment: itemComments.get(itemId),
            images: itemImages.get(itemId)
          });
          totalAmount += totalCost;
        }
      });

      setOrderSummary({
        supplier: selectedSupplier,
        items: orderItems,
        totalAmount
      });
    } else {
      setOrderSummary(null);
    }
  }, [selectedSupplier, selectedItems, allItems, itemComments, itemImages]);

  const handleToggleItem = (itemId: string) => {
    setSelectedItems(prev => {
      const updated = new Map(prev);
      if (updated.has(itemId)) {
        updated.delete(itemId);
      } else {
        updated.set(itemId, 1); // Default quantity of 1
      }
      return updated;
    });
  };

  const handleQuantityChange = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      // Remove item if quantity is 0 or less
      setSelectedItems(prev => {
        const updated = new Map(prev);
        updated.delete(itemId);
        return updated;
      });
    } else {
      setSelectedItems(prev => {
        const updated = new Map(prev);
        updated.set(itemId, quantity);
        return updated;
      });
    }
  };

  const handleReviewOrder = () => {
    if (!orderSummary) return;
    // Opened from the Create New Order screen — Back should return here.
    setSummaryFromCustomOrder(false);
    setShowOrderSummaryModal(true);
  };

  // Back from the order summary when it was reached via the custom order flow:
  // reopen the Create Custom Order screen pre-filled with everything the user
  // was working on (including quantity/comment edits made in the summary).
  const handleSummaryBackToCustomOrder = () => {
    const editable = customOrderItemIds
      .map((id) => {
        const itemData =
          temporaryItems.find((i) => i.id === id) || items.find((i) => i.id === id);
        if (!itemData) return null;
        return {
          itemId: id,
          quantity: selectedItems.get(id) ?? 1,
          itemData,
          comments: itemComments.get(id) || undefined,
          images: itemImages.get(id) || undefined,
        };
      })
      .filter((x): x is CustomOrderItemResult => x !== null);
    setShowOrderSummaryModal(false);
    setCustomOrderEditItems(editable);
    setIsNewStockItemModalOpen(true);
  };

  // State to track the last created order for email linking
  const [lastCreatedOrderId, setLastCreatedOrderId] = useState<string | null>(null);
  const [lastCreatedOrderNumber, setLastCreatedOrderNumber] = useState<string | null>(null);
  // Journey link: set when items were AI-generated from a field quote, so the
  // PO follows the customer (lead → field quote → booking) automatically.
  const [orderFieldQuoteId, setOrderFieldQuoteId] = useState<string | null>(null);
  // Manual journey link picked by the user (booking / field quote / lead).
  const [manualLink, setManualLink] = useState<OrderLinkSelection | null>(null);
  const [showLinkPicker, setShowLinkPicker] = useState(false);
  
  const handleSendOrder = async (orderNumber: string): Promise<boolean | string> => {
    if (!orderSummary || !selectedBusiness?.id) return false;

    try {
      setIsSubmitting(true);

      // Use the orderNumber passed from OrderSummaryPDFModal (ensures email and DB match)

      // Prepare order and order items for the database
      const order: Order = {
        business_id: selectedBusiness.id,
        supplier_id: orderSummary.supplier.id,
        order_number: orderNumber,
        total_amount: orderSummary.totalAmount,
        status: 'pending',
        project_id: projectId || null,
        // Manual pick wins; otherwise fall back to the field quote the items
        // were AI-generated from. The DB derives the remaining links.
        field_quote_id: manualLink?.type === 'field_quote' ? manualLink.id : orderFieldQuoteId,
        booking_id: manualLink?.type === 'booking' ? manualLink.id : null,
        pipeline_opportunities_id: manualLink?.type === 'lead' ? manualLink.id : null
      };

      const dbOrderItems: OrderItem[] = orderSummary.items.map(item => ({
        item_id: item.item.id,
        quantity: item.quantityToOrder,
        unit_price: item.item.acquisition_cost,
        total_price: item.totalCost,
        item_name: item.item.name,
        item_unit: item.item.unit,
        notes: item.comment?.trim() || undefined,
        images: item.images?.length ? item.images : undefined
      }));

      // Create order in database (the PO system assigns the real number)
      const createdOrder = await createOrder(order, dbOrderItems);
      const assignedNumber = createdOrder.order_number || orderNumber;
      
      // Store order info for email linking
      setLastCreatedOrderId(createdOrder.id);
      setLastCreatedOrderNumber(assignedNumber);

      if (onOrderCreated) {
        await onOrderCreated(createdOrder.id);
      }

      toast.success({
        title: 'Order Created',
        description: `Order ${assignedNumber} has been created successfully.`,
        duration: 5000,
      });

      // Don't close immediately - wait for email to be sent.
      // Return the DB-assigned PO number so the email/SMS use it.
      return assignedNumber;
    } catch (err) {
      console.error('Error sending order:', err);
      toast.error({
        title: 'Order Failed',
        description: err instanceof Error ? err.message : 'Failed to send order',
        duration: 5000,
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle email sent callback - link email to order
  const handleOrderEmailSent = async (orderNumber: string, messageId: string, threadId: string, emailBody: string) => {
    if (!selectedBusiness?.id || !lastCreatedOrderId) return;
    
    try {
      // Update order with email tracking info
      await updateOrderEmailInfo(lastCreatedOrderId, messageId, threadId);
      
      // Store the outgoing email in order_email_threads
      if (orderSummary?.supplier?.email) {
        await linkEmailToOrder(
          lastCreatedOrderId,
          selectedBusiness.id,
          orderSummary.supplier.id,
          orderSummary.supplier.email,
          `[ORDER-${orderNumber}] ${orderNumber}`,
          emailBody,
          messageId
        );
      }
      
      console.log('✅ Order email linked successfully:', lastCreatedOrderId, messageId, threadId);
    } catch (error) {
      console.error('Error linking email to order:', error);
    }
  };

  /**
   * Called by the summary modal after ANY successful send (email or SMS):
   * reset the whole flow and close every modal, so the user isn't left on a
   * stale Create New Order screen.
   */
  const handleOrderFlowComplete = () => {
    setSelectedSupplier(null);
    setSearchQuery('');
    setSelectedItems(new Map());
    setOrderSummary(null);
    setShowOrderSummaryModal(false);
    setLastCreatedOrderId(null);
    setLastCreatedOrderNumber(null);
    setOrderFieldQuoteId(null);
    setManualLink(null);
    onClose();
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <>
      {createPortal(
        <div
          className={`fixed inset-0 transition-opacity duration-200 ${
            isOpen ? 'bg-black/50' : 'bg-black/0'
          }`}
          style={{
            zIndex: 100020, // Higher than PlaceOrderModal (100010)
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
            paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
          }}
          onClick={onClose}
          onTransitionEnd={handleTransitionEnd}
        >
          <div
            className={`bg-white transform transition-all duration-200 ${
              isMobile
                ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
                : `fixed inset-y-0 right-0 w-full md:w-[700px] shadow-2xl ${
                    isOpen ? 'translate-x-0' : 'translate-x-full'
                  }`
            }`}
            style={{
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
              zIndex: 100060 // Higher z-index than PlaceOrderModal (100050)
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col overflow-hidden">
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <X className="h-5 w-5" />
            </button>

            {/* Header */}
            <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
              <h2 className="text-2xl font-bold text-gray-900" style={{
                letterSpacing: '-0.02em'
              }}>
                Create New Order
              </h2>
            </div>

            {/* Content */}
            <div
              className="flex-1 overflow-y-auto bg-white"
              style={{
                WebkitOverflowScrolling: 'touch',
                paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
              }}
            >
              <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6`}>
              {/* Supplier Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Select Supplier
                </label>
                {loading ? (
                  <div className="flex items-center justify-center h-12">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  </div>
                ) : (
                  <select
                    value={selectedSupplier?.id || ''}
                    onChange={(e) => {
                      const supplier = suppliers.find(s => s.id === e.target.value);
                      setSelectedSupplier(supplier || null);
                      setSearchQuery('');
                      setSelectedItems(new Map());
                    }}
                    className="w-full px-4 py-3 text-sm bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">-- Select a supplier --</option>
                    {suppliers.map(supplier => (
                      <option key={supplier.id} value={supplier.id}>
                        {`${supplier.first_name} ${supplier.last_name}`.trim()}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Journey link (optional) */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Link to Job <span className="font-normal text-gray-400">(optional)</span>
                </label>
                {manualLink && manualLink.type !== 'none' ? (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowLinkPicker(true)}
                      className="flex-1 flex items-center gap-2 px-4 py-3 text-sm bg-blue-50 border border-blue-200 rounded-xl text-blue-700 font-medium text-left hover:bg-blue-100 transition-colors touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <Link2 className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{manualLink.label}</span>
                    </button>
                    <button
                      onClick={() => setManualLink(null)}
                      className="h-11 w-11 flex items-center justify-center text-gray-400 hover:text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                      aria-label="Remove link"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowLinkPicker(true)}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm bg-white border border-dashed border-gray-300 rounded-xl text-gray-500 text-left hover:border-blue-400 hover:text-blue-600 transition-colors touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Link2 className="h-4 w-4 flex-shrink-0" />
                    Link to a booking, field quote, or lead
                  </button>
                )}
              </div>

              {/* Items List (only show if supplier selected) */}
              {selectedSupplier && (
                <>
                  {/* Search Bar and 1 Time Item Button */}
                  <div className="flex gap-3">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search items..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 text-sm bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <button
                      onClick={() => {
                        setCustomOrderEditItems(null);
                        setIsNewStockItemModalOpen(true);
                      }}
                      className="px-4 py-3 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 active:bg-blue-200 active:scale-[0.98] transition-all duration-150 whitespace-nowrap"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      + Custom Order
                    </button>
                  </div>

                  {/* Items List */}
                  {filteredItems.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p className="text-sm">
                        {searchQuery ? 'No items match your search' : 'No items available for this supplier'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredItems.map(item => {
                        const isSelected = selectedItems.has(item.id);
                        const quantity = selectedItems.get(item.id) || 0;
                        return (
                          <div
                            key={item.id}
                            className={`p-4 border rounded-xl transition-all ${
                              isSelected
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-start gap-4">
                              {/* Checkbox */}
                              <div className="flex items-center pt-1">
                                <label className="flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleToggleItem(item.id)}
                                    className="sr-only"
                                  />
                                  <div
                                    className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${
                                      isSelected
                                        ? 'bg-blue-600 border-blue-600'
                                        : 'bg-white border-gray-300'
                                    }`}
                                  >
                                    {isSelected && <Check className="h-4 w-4 text-white" />}
                                  </div>
                                </label>
                              </div>

                              {/* Item Info */}
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-semibold text-gray-900">{item.name}</h4>
                                {item.description && (
                                  <p className="mt-1 text-xs text-gray-600 line-clamp-2">
                                    {item.description}
                                  </p>
                                )}
                                <div className="mt-2 flex items-center gap-4 text-xs text-gray-600">
                                  <span>Current: {item.quantity} {item.unit}</span>
                                  {effectiveOnOrderQuantities[item.id] > 0 && (
                                    <span className="text-blue-600 font-semibold">
                                      On Order: {effectiveOnOrderQuantities[item.id]} {item.unit}
                                    </span>
                                  )}
                                  <span>Cost: ${item.acquisition_cost.toFixed(2)}/{item.unit}</span>
                                </div>
                              </div>

                              {/* Quantity Input (only show if selected) */}
                              {isSelected && (
                                <div className="flex items-center gap-2">
                                  <label className="text-xs text-gray-600 whitespace-nowrap">Qty:</label>
                                  <input
                                    type="number"
                                    min="1"
                                    value={quantity}
                                    onChange={(e) =>
                                      handleQuantityChange(item.id, parseInt(e.target.value) || 0)
                                    }
                                    className="w-20 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  />
                                  <span className="text-xs text-gray-500">{item.unit}</span>
                                  <span className="text-sm font-semibold text-gray-900 ml-2">
                                    ${(quantity * item.acquisition_cost).toFixed(2)}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
              </div>
            </div>

            {/* Order Summary Footer */}
            {orderSummary && orderSummary.items.length > 0 && (
              <div className="border-t border-gray-200 p-6 bg-gray-50 flex-shrink-0">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Order Summary</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {orderSummary.items.length} item{orderSummary.items.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <p className="text-lg font-semibold text-blue-600">
                    ${orderSummary.totalAmount.toFixed(2)}
                  </p>
                </div>
                <button
                  onClick={handleReviewOrder}
                  disabled={isSubmitting}
                  className="w-full p-3.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-all duration-150 active:scale-[0.98] flex items-center justify-center gap-2 shadow-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <ShoppingCart className="h-5 w-5" />
                  Review Order
                </button>
              </div>
            )}

            {/* Order Summary Modal */}
            {orderSummary && (
              <OrderSummaryPDFModal
                isOpen={showOrderSummaryModal}
                onClose={() => setShowOrderSummaryModal(false)}
                onBack={
                  summaryFromCustomOrder && customOrderItemIds.length > 0
                    ? handleSummaryBackToCustomOrder
                    : undefined
                }
                supplier={orderSummary.supplier}
                items={orderSummary.items}
                totalAmount={orderSummary.totalAmount}
                onSendOrder={handleSendOrder}
                onEmailSent={handleOrderEmailSent}
                onOrderSent={handleOrderFlowComplete}
                onItemsChange={(updatedItems, newTotal) => {
                  // Update order summary with new quantities/comments
                  setOrderSummary({
                    supplier: orderSummary.supplier,
                    items: updatedItems,
                    totalAmount: newTotal
                  });
                  // Also update selectedItems and comments in the parent
                  const newSelectedItems = new Map<string, number>();
                  const newComments = new Map<string, string>();
                  const newImages = new Map<string, string[]>();
                  updatedItems.forEach(({ item, quantityToOrder, comment, images }) => {
                    if (quantityToOrder > 0) {
                      newSelectedItems.set(item.id, quantityToOrder);
                    }
                    if (comment?.trim()) {
                      newComments.set(item.id, comment);
                    }
                    if (images?.length) {
                      newImages.set(item.id, images);
                    }
                  });
                  setSelectedItems(newSelectedItems);
                  setItemComments(newComments);
                  setItemImages(newImages);
                }}
              />
            )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* One-Time Item Order Modal */}
      {selectedSupplier && (
        <OneTimeItemOrderModal
          isOpen={isNewStockItemModalOpen}
          onClose={() => setIsNewStockItemModalOpen(false)}
          supplier={selectedSupplier}
          initialItems={customOrderEditItems}
          onSuccess={async (createdItems, meta) => {
            console.log('🎯 Custom order items created/updated, adding to order:', createdItems);
            setIsNewStockItemModalOpen(false);
            setCustomOrderEditItems(null);
            const removedIds = new Set(meta?.removedItemIds || []);
            if (createdItems.length === 0 && removedIds.size === 0) return;
            if (meta?.fieldQuoteId) setOrderFieldQuoteId(meta.fieldQuoteId);

            // Make the items available to the order summary calculation —
            // replacing stale copies after edits and dropping removed ones.
            setTemporaryItems(prev => {
              const merged = prev
                .filter(i => !removedIds.has(i.id))
                .map(i => createdItems.find(c => c.itemId === i.id)?.itemData || i);
              createdItems.forEach(({ itemData }) => {
                if (!merged.find(i => i.id === itemData.id)) merged.push(itemData);
              });
              return merged;
            });
            setSelectedItems(prev => {
              const updated = new Map(prev);
              removedIds.forEach(id => updated.delete(id));
              createdItems.forEach(({ itemId, quantity }) => updated.set(itemId, quantity));
              return updated;
            });
            setItemComments(prev => {
              const updated = new Map(prev);
              removedIds.forEach(id => updated.delete(id));
              createdItems.forEach(({ itemId, comments }) => {
                if (comments) updated.set(itemId, comments);
                else updated.delete(itemId);
              });
              return updated;
            });
            setItemImages(prev => {
              const updated = new Map(prev);
              removedIds.forEach(id => updated.delete(id));
              createdItems.forEach(({ itemId, images }) => {
                if (images?.length) updated.set(itemId, images);
                else updated.delete(itemId);
              });
              return updated;
            });
            setCustomOrderItemIds(prev => [
              ...prev.filter(id => !removedIds.has(id) && !createdItems.some(c => c.itemId === id)),
              ...createdItems.map(c => c.itemId),
            ]);
            if (createdItems.length > 0) {
              setSummaryFromCustomOrder(true);
              setShouldAutoOpenSummary(true);
            }

            // Edit passes already toast "Order Updated" from the modal itself.
            if (!customOrderEditItems?.length && createdItems.length > 0) {
              toast.success({
                title: createdItems.length === 1 ? 'Item Added' : 'Items Added',
                description: createdItems.length === 1
                  ? `${createdItems[0].itemData.name} has been added to your order`
                  : `${createdItems.length} items have been added to your order`,
                duration: 3000,
              });
            }

            // Refresh items list if callback provided (to ensure it appears in future refreshes)
            if (onItemsRefresh) {
              await onItemsRefresh();
            }
          }}
        />
      )}

      {/* Journey link picker */}
      <LinkOrderTargetModal
        isOpen={showLinkPicker}
        onClose={() => setShowLinkPicker(false)}
        onSelect={(sel) => setManualLink(sel.type === 'none' ? null : sel)}
      />
    </>
  );
}